import './settings.css'
import {
    IonAlert,
    IonContent,
    IonIcon,
    IonItem,
    IonPage,
    IonRange,
    IonSelect,
    IonSelectOption,
    IonToggle,
} from '@ionic/react'
import { informationCircleOutline, warning } from 'ionicons/icons'
import React, { useEffect, useState } from 'react'

import {
  PRESET_CUSTOM,
  SEASON_RULE_MALE_MIXED,
  defaultPreset,
  presets,
  queryMaxIdealPositions,
  sliderSettings,
} from './definitions'
import { useMyGameFormat, useMyNumPeriods, useMySeasonRules, useSettingsState, useUserHasPro } from './data'
import FadeText from './FadeText'
import Header from './Header'
import MaleIcon from './MaleIcon'
import SampleGoButton from './SampleGoButton'

//
// Create an object for all tooltips
//
const tooltips: {
	[key in SettingsSliderType]?: { desc: string, tip: string }
} = { }

sliderSettings.forEach(setting => {
	const { id, desc, tip } = setting
	tooltips[id] = { desc, tip: tip ?? '' }
})

//completeTooltips = tooltips as { [key in SettingsSliderType]: { desc: string, tip: string } };

function queryValueForPreset(preset: keyof typeof presets, setting: SettingsSliderType) {
	return presets[preset].defaults[setting]
}

const Settings = () => {

	const [ settings, setSettings ] = useSettingsState()
	const userHasPro = useUserHasPro()
	const numPeriods = useMyNumPeriods()
	const gameFormat = useMyGameFormat()
	const seasonRules = useMySeasonRules()
	const maxIdealPositions = queryMaxIdealPositions(numPeriods, gameFormat)

	const [ tooltip, setTooltip ] = useState<SettingsSliderType>()

    // Chrome has a problem where dragging a slider knob and releasing it while
    // not on the same track will update both sliders at once. Don't allow that.
	const [ activeSlider, setActiveSlider ] = useState<SettingsSliderType>()

	const [ useStackedSliderLabels, setUseStackedSliderLabels ] = useState(() => {
		if (typeof window === 'undefined') {
			return false
		}
		return window.matchMedia('(max-width: 640px)').matches
	})

	useEffect(() => {
		if (typeof window === 'undefined') {
			return
		}

		const mediaQuery = window.matchMedia('(max-width: 640px)')
		const onChange = (e: MediaQueryListEvent) => {
			setUseStackedSliderLabels(e.matches)
		}

		setUseStackedSliderLabels(mediaQuery.matches)
		mediaQuery.addEventListener('change', onChange)

		return () => mediaQuery.removeEventListener('change', onChange)
	}, [ ])

	const currentPresetId = settings.preset || defaultPreset
    const currentPreset = presets[currentPresetId]

	/*
	const valuesForPreset = useMemo(() => {
		return Object.keys(presets).reduce((obj, id) => {
			obj[id] = sliderSettings.reduce((map, setting) => {
				map[setting.id] = presets[id].defaults[setting.id] ?? setting.default
				return map
			}, { })
			return obj
		}, { })
	}, [ ])
	*/

	const changeValue = (obj: { id: keyof typeof settings, value: Settings[keyof typeof settings] }) => {

		const { id, value } = obj

		let newSettings = {
			...settings,
			[id]: value,
		}

		if (id in currentPreset.defaults && value !== currentPreset.defaults[id as SettingsSliderType]) {

			// When manually changing a slider, set the preset to CUSTOM
			//

			console.log("Forcing change to CUSTOM")
			newSettings.preset = PRESET_CUSTOM

		} else if (id === 'preset' && value !== 'CUSTOM') {
			//
			// When selecting a preset, return all the values to default
			//

			sliderSettings.forEach(setting => {
				newSettings[setting.id] = queryValueForPreset(value as SettingsPresetType, setting.id)
			})
		}

		setSettings(newSettings)
	}

	const sortedPresetIds = Object.entries(presets).sort((a, b) => a[1].order - b[1].order).map(([ id, _ ]) => id as SettingsPresetType)

	return (
		<IonPage>
			<IonContent className="ion-padding settings-content">
				<section className="settings">

					<IonItem>
						<IonIcon
							className="preset-icon"
							icon={currentPreset.icon}
							slot="end"
						/>

						<IonSelect
							label="Preset"
							interface="action-sheet"
							value={currentPresetId}
							onIonChange={ (e: CustomEvent) => changeValue({
								id: 'preset',
								value: e.detail.value,
							})}
						>
							{
								sortedPresetIds.map(id =>
									<IonSelectOption
										value={id}
										key={id}
									>
										<IonIcon icon={presets[id].icon} />
										{presets[id].name}
									</IonSelectOption>
								)
							}
						</IonSelect>
					</IonItem>

					<IonItem lines="none">
						<IonToggle
							justify="space-between"
							checked={!!settings.showNetStats}
							onIonChange={ () => changeValue({ id: 'showNetStats', value: !settings.showNetStats })}
						>
							Show NetStats
						</IonToggle>
					</IonItem>

					<IonItem disabled={!settings.showNetStats || !userHasPro}>
						<IonToggle
							justify="space-between"
							checked={!!settings.showNetStats && !!settings.showNetStatsCombos && userHasPro}
							onIonChange={ () => changeValue({ id: 'showNetStatsCombos', value: !settings.showNetStatsCombos })}
						>
							... and Pair/Trio Stats
							{
								!userHasPro && (
									<span className="pro">PRO</span>
								)
							}
						</IonToggle>
					</IonItem>

					{
						seasonRules === SEASON_RULE_MALE_MIXED && (
							<IonItem lines="none">
								<p className="settings-sliders-explanation settings-rules-explanation">
									Season setting: 1 male per third.
									<MaleIcon />
								</p>
							</IonItem>
						)
					}

					<IonItem lines="none">
						<p className="settings-sliders-explanation">
							Sliders tell AutoRoster how to balance different priorities when you use <SampleGoButton />.
						</p>
					</IonItem>

					<SliderWarning
						values={settings}
					/>

					{
						sliderSettings.map(setting => {

							if (setting.id === 'idealNumPositions') {

								const displayValue = Math.min(settings.idealNumPositions ?? setting.default, maxIdealPositions)

								return (
									<IonItem
										key={setting.id}
										className="setting setting-ideal-num-pos"
										lines="none"
									>
										<div
											className="setting-desc ion-text-wrap"
											onClick={(e) => {
												e.stopPropagation()
												setTooltip(setting.id)
											}}
										>
											{setting.desc}
											<IonIcon
												icon={informationCircleOutline}
												className="tooltip-icon"
											/>
										</div>
										<IonSelect
											interface="action-sheet"
											interfaceOptions={{
												header: setting.desc,
												subHeader: setting.tip,
											}}
											value={displayValue}
											slot="end"
											onIonChange={(e: CustomEvent) => changeValue({ id: 'idealNumPositions', value: e.detail.value })}
										>
											{Array.from({ length: maxIdealPositions }, (_, i) => (
												<IonSelectOption key={i + 1} value={i + 1}>
													{i + 1} per game
												</IonSelectOption>
											))}
										</IonSelect>
									</IonItem>
								)
							}

								return (
									<IonItem
										key={setting.id}
										className={`setting setting-slider setting-slider-id-${setting.id} ${activeSlider && activeSlider !== setting.id ? 'setting-slider-inactive' : ''}`}
										lines="none"
									>
										<IonRange
											labelPlacement={useStackedSliderLabels ? 'stacked' : 'start'}
										// pin={true}
                                        ticks={true}
                                        snaps={true}
										// pinFormatter={value => `${value}`}
										// defaultValue={initialSliderValues[setting.id]}
											value={settings[setting.id]}
											min={0}
											max={10}
											onIonKnobMoveStart={() => setActiveSlider(setting.id)}
											onIonKnobMoveEnd={() => setActiveSlider(undefined)}
											onIonChange={(e: CustomEvent) => {
												const { value } = e.detail
												changeValue({
												id: setting.id,
												value: typeof value === 'number' ? value : value.lower
											})
										}}
									>
										<div
											slot="label"
											className="setting-desc ion-text-wrap"
											onClick={() => setTooltip(setting.id)}
										>
											{setting.desc}
                                            {setting.tip && (
                                                <IonIcon
                                                    icon={informationCircleOutline}
                                                    className="tooltip-icon"
                                                />
                                            )}
										</div>
									</IonRange>
								</IonItem>
							)
						})
					}

					<Tooltip
						tooltip={tooltip}
						onDismiss={() => setTooltip(undefined)}
					/>
				</section>
			</IonContent>

			<Header
				allowTeamChange={false}
			/>

		</IonPage>
	)
}

type TooltipProps = {
	tooltip: keyof typeof tooltips | undefined
	onDismiss: () => void
}

const Tooltip = (props: TooltipProps) => {

    const { tooltip, onDismiss } = props

	const t = tooltip ? tooltips[tooltip] : undefined
	const desc = t ? t.desc : undefined
	const tip = t ? t.tip : undefined

    return (
        <IonAlert
            isOpen={!!tooltip}
            onDidDismiss={onDismiss}
            header={desc}
            message={tip}
            buttons={[ 'OK' ]}
        />
    )
}

type SliderWarningProps = {
	values: Settings
}

const SliderWarning = (props: SliderWarningProps) => {

	const { values } = props

	const excludedSettings: SettingsSliderType[] = [ 'useRollingSubs' ]
	const numHighValues = Object.entries(values).filter(([ key, value ]) => {
		return typeof value === 'number' && value >= 9 && !excludedSettings.includes(key as SettingsSliderType)
	}).length

	if (numHighValues < 2)
		return null

	return (
		<FadeText
			isBlock={true}
			contentKey="slider-warning"
		>
			<IonItem lines="none">
				<p className="settings-sliders-warning">
					<IonIcon icon={warning} slot="start" />
					Multiple sliders are set to very high values. It's best to move a slider to
					the far right only if you want AutoRoster to prioritize that far above everything else.
				</p>
			</IonItem>
		</FadeText>
	)
}

export default Settings
